// src/app/modules/adminrh/Offboarding/OffboardingJS/Offboarding.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Calendar,
    CheckCircle,
    Clock,
    User,
    FileText,
    Key,
    Package,
    AlertCircle,
    Search,
    Plus,
    X,
    Trash2,
} from "lucide-react";
import Swal from "sweetalert2";
import "../OffboardingStyles/Offboarding.css";


// ✅ LISTS (déjà chez toi)
import { getAllUsersRequest } from "../../../../redux/actions/userActions";
import { getAllJobsRequest } from "../../../../redux/actions/Jobactions";
import { getAllEntitesRequest } from "../../../../redux/actions/entitesActions";




// ✅ OFFBOARDING (actions que tu m'as donnés)
import {
    fetchOffboardingsRequest,
    getOffboardingRequest,
    createOffboardingRequest,
    updateOffboardingRequest,
    deleteOffboardingRequest,
    fetchEtapesRequest,
    createEtapeRequest,
    updateEtapeRequest,
    deleteEtapeRequest,
} from "../../../../redux/actions/offboardingActions";

/** =========================
 * Modal Component (style attestation)
 * ========================= */
const Modal = ({ open, title, children, footer, onClose }) => {
    if (!open) return null;
    return (
        <div className="obModal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div className="obModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="obModal__header">
                    <div className="obModal__title">{title}</div>
                    <button className="obModal__close" onClick={onClose} aria-label="Fermer">
                        <X size={18} />
                    </button>
                </div>
                <div className="obModal__body">{children}</div>
                {footer && <div className="obModal__footer">{footer}</div>}
            </div>
        </div>
    );
};

/** =========================
 * SearchableSelect (input + dropdown filter)
 * ========================= */
function SearchableSelect({
                              label,
                              placeholder = "Rechercher...",
                              options = [],
                              value,
                              onChange,
                              disabled = false,
                          }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const wrapRef = useRef(null);

    const selected = useMemo(
        () => options.find((o) => String(o.value) === String(value)) || null,
        [options, value]
    );

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return options;
        return options.filter((o) => (o.label || "").toLowerCase().includes(s));
    }, [q, options]);

    useEffect(() => {
        const onDoc = (e) => {
            if (!wrapRef.current) return;
            if (!wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const inputValue = open ? q : "";

    return (
        <div className="obField" ref={wrapRef}>
            {label && <label>{label}</label>}

            <div className={`obSS ${disabled ? "is-disabled" : ""}`}>
                <input
                    className="obSSInput"
                    type="text"
                    disabled={disabled}
                    placeholder={selected ? selected.label : placeholder}
                    value={inputValue}
                    onFocus={() => !disabled && setOpen(true)}
                    onChange={(e) => {
                        setQ(e.target.value);
                        setOpen(true);
                    }}
                />

                <button
                    type="button"
                    className="obSSBtn"
                    onClick={() => !disabled && setOpen((v) => !v)}
                    disabled={disabled}
                    title="Ouvrir"
                >
                    ▾
                </button>

                {open && !disabled && (
                    <div className="obSSList">
                        {filtered.length === 0 ? (
                            <div className="obSSEmpty">Aucun résultat</div>
                        ) : (
                            filtered.map((o) => (
                                <button
                                    key={o.value}
                                    type="button"
                                    className={`obSSItem ${
                                        String(o.value) === String(value) ? "is-active" : ""
                                    }`}
                                    onClick={() => {
                                        onChange(o.value, o);
                                        setOpen(false);
                                        setQ("");
                                    }}
                                >
                                    {o.label}
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/** =========================
 * Mapping UI -> Backend enums
 * ========================= */
const RAISON_UI = [
    "Démission",
    "Fin de contrat",
    "Retraite",
    "Licenciement",
    "Mutation",
];
const RAISON_TO_BACK = {
    Démission: "DEMISSION",
    "Fin de contrat": "FIN_DE_CONTRAT",
    Retraite: "RETRAITE",
    Licenciement: "LICENCIEMENT",
    Mutation: "MUTATION",
};

const RAISON_FROM_BACK = (v) => {
    if (!v) return "Démission";
    switch (String(v).toUpperCase()) {
        case "DEMISSION":
            return "Démission";
        case "FIN_DE_CONTRAT":
            return "Fin de contrat";
        case "RETRAITE":
            return "Retraite";
        case "LICENCIEMENT":
            return "Licenciement";
        case "MUTATION":
            return "Mutation";
        default:
            return "Démission";
    }
};

const STATUS_TEXT = (status) => {
    const s = String(status || "").toLowerCase();
    if (s.includes("term")) return "Terminé";
    if (s.includes("plan")) return "Planifié";
    return "En cours";
};
const STATUS_CLASS = (status) => {
    const s = String(status || "").toLowerCase();
    if (s.includes("term")) return "obBadge obBadge--green";
    if (s.includes("plan")) return "obBadge obBadge--gray";
    return "obBadge obBadge--blue";
};

// ✅ helpers statut étape
const ETAPE_IS_DONE = (st) => String(st || "").toUpperCase() === "COMPLETE";

export default function Offboarding() {
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState("dashboard");
    const [showModal, setShowModal] = useState(false);
    const [selectedOffboardingId, setSelectedOffboardingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    // ✅ NEW: filtres (collaborateur / poste / département)
    const [filters, setFilters] = useState({
        employeeId: "",
        jobId: "",
        entityId: "",
    });
    const FONT_STACK =
        'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, "Noto Sans", "Liberation Sans", sans-serif';

    const ui = useMemo(
        () => ({
            primary: "#2f4595",
            danger: "#dc3545",
            text: "#111827",
            muted: "#6b7280",
            border: "#e5e7eb",
            soft: "#f9fafb",
            font: FONT_STACK,
        }),
        []
    );

    const clearFilters = () => setFilters({ employeeId: "", jobId: "", entityId: "" });

    // ✅ lists
    const userState = useSelector((state) => state.user);
    const jobsState = useSelector((state) => state.jobs);
    const entitesState = useSelector((state) => state.entites);
    const users = useSelector((s) => s.user?.users || []);
    const jobs = jobsState?.jobs || [];
    const entites = entitesState?.entites || [];
    const loadingLists = !!(userState?.loading || jobsState?.loading || entitesState?.loading);

    // ✅ offboarding slice
    const offState = useSelector((s) => s.offboarding || {});
    const offLoading = !!offState.loading;
    const offboardings = offState.offboardings || [];
    const selectedOffboarding = offState.selectedOffboarding;
    const etapesByOffboarding = offState.etapesByOffboarding || {};

    const selectedEtapes = useMemo(() => {
        if (!selectedOffboardingId) return [];
        return etapesByOffboarding[selectedOffboardingId] || [];
    }, [etapesByOffboarding, selectedOffboardingId]);

    // ====== bootstrap
    useEffect(() => {
        dispatch(getAllUsersRequest());
        dispatch(getAllJobsRequest());
        dispatch(getAllEntitesRequest());
        dispatch(fetchOffboardingsRequest());
    }, [dispatch]);

    // ====== options
    const employeeOptions = useMemo(() => {
        const arr = Array.isArray(users) ? users : [];
        return [...arr]
            .map((u) => ({
                value: u.id,
                label:
                    u.fullName ||
                    `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
                    u.username ||
                    u.email ||
                    `User #${u.id}`,
                data: u,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [users]);

    const managerOptions = useMemo(() => {
        const arr = Array.isArray(users) ? users : [];
        const hasRoles = arr.some((u) => Array.isArray(u.roles) && u.roles.length > 0);

        const list = hasRoles
            ? arr.filter((u) =>
                (u.roles || []).some((r) => {
                    const n = String(r?.name || r?.code || "").toUpperCase();
                    return n.includes("MANAGER");
                })
            )
            : arr;

        return [...list]
            .map((u) => ({
                value: u.id,
                label:
                    u.fullName ||
                    `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
                    u.username ||
                    u.email ||
                    `User #${u.id}`,
                data: u,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [users]);

    const positionOptions = useMemo(() => {
        return [...jobs]
            .map((j) => ({
                value: j.id,
                label: j.name || j.nom || j.libelle || `Poste #${j.id}`,
                data: j,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [jobs]);

    const departmentOptions = useMemo(() => {
        return [...entites]
            .map((e) => ({
                value: e.id,
                label: e.name || e.nom || e.libelle || `Entité #${e.id}`,
                data: e,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [entites]);

    // ====== maps
    const userById = useMemo(() => {
        const m = new Map();
        (Array.isArray(users) ? users : []).forEach((u) => m.set(String(u.id), u));
        return m;
    }, [users]);

    const jobById = useMemo(() => {
        const m = new Map();
        (Array.isArray(jobs) ? jobs : []).forEach((j) => m.set(String(j.id), j));
        return m;
    }, [jobs]);

    const entById = useMemo(() => {
        const m = new Map();
        (Array.isArray(entites) ? entites : []).forEach((e) => m.set(String(e.id), e));
        return m;
    }, [entites]);

    const getUserLabel = (id) => {
        const u = userById.get(String(id));
        if (!u) return `User #${id}`;
        return (
            u.fullName ||
            `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
            u.username ||
            u.email ||
            `User #${id}`
        );
    };

    const getJobLabel = (jobId) => {
        if (!jobId) return "—";
        const j = jobById.get(String(jobId));
        return j?.name || j?.nom || j?.libelle || `Poste #${jobId}`;
    };

    const getEntLabel = (entityId) => {
        if (!entityId) return "—";
        const e = entById.get(String(entityId));
        return e?.name || e?.nom || e?.libelle || `Entité #${entityId}`;
    };

    const ETAPE_STATUS_OPTIONS = [
        { value: "EN_ATTENTE", label: "En attente" },
        { value: "COMPLETE", label: "Complété" },
    ];

    // ✅ NEW: filtres dépendants
    const selectedUser = useMemo(() => {
        if (!filters.employeeId) return null;
        return userById.get(String(filters.employeeId)) || null;
    }, [filters.employeeId, userById]);

    const selectedUserJobId = selectedUser?.jobId ?? selectedUser?.job_id ?? "";
    const selectedUserEntityId = selectedUser?.entityId ?? selectedUser?.entity_id ?? "";

    const employeeOptionsFiltered = useMemo(() => {
        let list = [...employeeOptions];

        if (filters.jobId) {
            list = list.filter((o) => {
                const u = o.data;
                const jid = u?.jobId ?? u?.job_id ?? "";
                return String(jid) === String(filters.jobId);
            });
        }

        if (filters.entityId) {
            list = list.filter((o) => {
                const u = o.data;
                const eid = u?.entityId ?? u?.entity_id ?? "";
                return String(eid) === String(filters.entityId);
            });
        }

        return list;
    }, [employeeOptions, filters.jobId, filters.entityId]);

    // ✅✅ CHANGEMENT: filtrer aussi sur filters.jobId (cascade complet)
    const positionOptionsFiltered = useMemo(() => {
        // si un job est choisi, ne montrer que ce job
        if (filters.jobId) {
            return positionOptions.filter((o) => String(o.value) === String(filters.jobId));
        }

        if (filters.employeeId && selectedUserJobId) {
            return positionOptions.filter((o) => String(o.value) === String(selectedUserJobId));
        }

        if (filters.entityId) {
            const possibleJobIds = new Set(
                (Array.isArray(users) ? users : [])
                    .filter(
                        (u) =>
                            String(u?.entityId ?? u?.entity_id ?? "") === String(filters.entityId)
                    )
                    .map((u) => String(u?.jobId ?? u?.job_id ?? ""))
                    .filter(Boolean)
            );
            return positionOptions.filter((o) => possibleJobIds.has(String(o.value)));
        }

        return positionOptions;
    }, [filters.jobId, filters.employeeId, selectedUserJobId, positionOptions, filters.entityId, users]);

    // ✅✅ CHANGEMENT: filtrer aussi sur filters.entityId (cascade complet)
    const departmentOptionsFiltered = useMemo(() => {
        // si une entité est choisie, ne montrer que cette entité
        if (filters.entityId) {
            return departmentOptions.filter((o) => String(o.value) === String(filters.entityId));
        }

        if (filters.employeeId && selectedUserEntityId) {
            return departmentOptions.filter((o) => String(o.value) === String(selectedUserEntityId));
        }

        if (filters.jobId) {
            const possibleEntIds = new Set(
                (Array.isArray(users) ? users : [])
                    .filter((u) => String(u?.jobId ?? u?.job_id ?? "") === String(filters.jobId))
                    .map((u) => String(u?.entityId ?? u?.entity_id ?? ""))
                    .filter(Boolean)
            );
            return departmentOptions.filter((o) => possibleEntIds.has(String(o.value)));
        }

        return departmentOptions;
    }, [filters.entityId, filters.employeeId, selectedUserEntityId, departmentOptions, filters.jobId, users]);

    const onPickEmployee = (val, opt) => {
        setFilters((p) => {
            const u = opt?.data || userById.get(String(val));
            const jid = u?.jobId ?? u?.job_id ?? "";
            const eid = u?.entityId ?? u?.entity_id ?? "";
            return {
                ...p,
                employeeId: val,
                jobId: jid ? String(jid) : p.jobId,
                entityId: eid ? String(eid) : p.entityId,
            };
        });
    };

    const onPickJob = (val) => {
        setFilters((p) => ({ ...p, jobId: val, employeeId: "" }));
    };

    const onPickEntity = (val) => {
        setFilters((p) => ({ ...p, entityId: val, employeeId: "" }));
    };

    // ====== modal form
    const emptyForm = {
        employeeId: "",
        departureDate: "",
        reason: "Démission",
        notes: "",
    };
    const [form, setForm] = useState(emptyForm);

    const openModal = () => {
        setForm(emptyForm);
        // ✅ reset filtres du modal à l'ouverture (optionnel mais clean)
        clearFilters();
        setShowModal(true);
    };
    const closeModal = () => setShowModal(false);
    const handleForm = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const canCreate = !!form.employeeId && !!form.departureDate;

    const createOffboarding = () => {
        if (!canCreate) {
            Swal.fire({
                icon: "warning",
                title: "",
                text: "Veuillez choisir un collaborateur et une date de départ.",
            });
            return;
        }

        dispatch(
            createOffboardingRequest({
                employeeId: Number(form.employeeId),
                dateDepart: form.departureDate,
                raisonDepart: RAISON_TO_BACK[form.reason] || "DEMISSION",
                commentaire: form.notes || "",
                progression: 0,
            })
        );

        closeModal();
    };

    // ====== open details
    const openDetails = (off) => {
        const id = off?.id;
        if (!id) return;
        setSelectedOffboardingId(id);
        setActiveTab("details");

        dispatch(getOffboardingRequest(id));
        dispatch(fetchEtapesRequest(id));
    };

    // ====== list rows
    const rows = useMemo(() => {
        const list = Array.isArray(offboardings) ? offboardings : [];
        return list.map((o) => {
            const employeeId = o.employeeId ?? o.employee_id ?? o.employee?.id ?? o.employee;
            const u = employeeId ? userById.get(String(employeeId)) : null;

            const jobId = u?.jobId || u?.job_id || null;
            const entityId = u?.entityId || u?.entity_id || null;

            const depDate = o.dateDepart || o.date_depart || o.departureDate || o.departure_date;
            const raison = o.raisonDepart || o.raison_depart || o.reason;
            const prog = o.progression ?? o.progress ?? 0;

            const status = o.statut || o.status || (prog >= 100 ? "termine" : "en_cours");

            return {
                raw: o,
                id: o.id,
                employeeId,
                employeeName: employeeId ? getUserLabel(employeeId) : "—",
                jobLabel: getJobLabel(jobId),
                entityLabel: getEntLabel(entityId),
                departureDate: depDate,
                raisonUi: RAISON_FROM_BACK(raison),
                commentaire: o.commentaire || o.notes || "",
                progression: Number(prog || 0),
                status,
            };
        });
    }, [offboardings, userById, jobById, entById]);

    const filteredRows = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(
            (r) =>
                (r.employeeName || "").toLowerCase().includes(q) ||
                (r.entityLabel || "").toLowerCase().includes(q) ||
                (r.jobLabel || "").toLowerCase().includes(q)
        );
    }, [rows, searchTerm]);

    // ====== stats
    const stats = useMemo(() => {
        const total = rows.length;
        const termines = rows.filter(
            (r) => String(r.status).toLowerCase().includes("term") || r.progression >= 100
        ).length;
        const planifies = rows.filter((r) => String(r.status).toLowerCase().includes("plan")).length;
        const enCours = total - termines - planifies;
        return { total, enCours, planifies, termines };
    }, [rows]);

    // ====== progression from steps
    const computeProgressFromEtapes = (etapes) => {
        const list = Array.isArray(etapes) ? etapes : [];
        const total = list.length;
        if (total === 0) return 0;

        const done = list.filter((e) => ETAPE_IS_DONE(e.statut)).length;
        return Math.round((done / total) * 100);
    };

    const refreshSelected = () => {
        if (!selectedOffboardingId) return;
        dispatch(getOffboardingRequest(selectedOffboardingId));
        dispatch(fetchEtapesRequest(selectedOffboardingId));
    };

    // ====== Etape UI state
    const [etapeDraft, setEtapeDraft] = useState({
        nom: "",
        ordreEtape: "",
        statut: "EN_ATTENTE",
    });

    const addEtape = () => {
        const nom = etapeDraft.nom.trim();
        if (!selectedOffboardingId || !nom) return;

        const nextOrder =
            etapeDraft.ordreEtape !== ""
                ? Number(etapeDraft.ordreEtape)
                : selectedEtapes?.length
                    ? Math.max(...selectedEtapes.map((e) => e.ordreEtape ?? 0)) + 1
                    : 1;

        const statutToSend = etapeDraft.statut || "EN_ATTENTE";

        dispatch(
            createEtapeRequest({
                offboardingId: selectedOffboardingId,
                employeeId:
                    selectedOffboarding?.employeeId ??
                    selectedOffboarding?.employee_id ??
                    selectedOffboarding?.employee ??
                    null,
                nom,
                ordreEtape: Number(nextOrder),
                statut: statutToSend,
            })
        );

        const optimisticEtapes = [...(selectedEtapes || []), { id: "__tmp__", statut: statutToSend }];
        const nextProg = computeProgressFromEtapes(optimisticEtapes);

        dispatch(
            updateOffboardingRequest(selectedOffboardingId, {
                progression: nextProg,
            })
        );

        setEtapeDraft({ nom: "", ordreEtape: "", statut: "EN_ATTENTE" });
    };

    const completeEtapeOnClick = (etape) => {
        if (!etape?.id) return;

        const cur = String(etape.statut || "").toUpperCase();
        if (cur === "COMPLETE") return;

        const next = "COMPLETE";

        dispatch(
            updateEtapeRequest(etape.id, {
                statut: next,
            })
        );

        const nextEtapes = (selectedEtapes || []).map((e) =>
            e.id === etape.id ? { ...e, statut: next } : e
        );
        const nextProg = computeProgressFromEtapes(nextEtapes);

        dispatch(
            updateOffboardingRequest(selectedOffboardingId, {
                progression: nextProg,
            })
        );
    };

    const removeEtape = (etape) => {
        if (!etape?.id || !selectedOffboardingId) return;

        dispatch(deleteEtapeRequest(etape.id, selectedOffboardingId));

        const nextEtapes = (selectedEtapes || []).filter((e) => e.id !== etape.id);
        const nextProg = computeProgressFromEtapes(nextEtapes);

        dispatch(
            updateOffboardingRequest(selectedOffboardingId, {
                progression: nextProg,
            })
        );
    };

    const removeOffboarding = (id) => {
        if (!id) return;
        dispatch(deleteOffboardingRequest(id));
        if (selectedOffboardingId === id) {
            setSelectedOffboardingId(null);
            setActiveTab("employees");
        }
    };

    /** ================= Dashboard View ================= */
    const DashboardView = () => {
        const avatarLetters = (fullName) =>
            String(fullName || "")
                .split(" ")
                .filter(Boolean)
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2);

        const daysLeft = (dateStr) => {
            if (!dateStr) return null;
            const d = new Date(dateStr);
            if (Number.isNaN(d.getTime())) return null;
            return Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
        };

        const upcoming = rows
            .filter((r) => r.departureDate)
            .slice()
            .sort((a, b) => new Date(a.departureDate) - new Date(b.departureDate))
            .slice(0, 3);

        return (
            <div className="obDashboard">
                <div className="obSectionIntro">
                    <div>
                        <h2 className="obCard__title">Tableau de bord</h2>
                        <p className="obMuted">Vue rapide des départs, des dossiers en cours et des prochaines échéances.</p>
                    </div>
                </div>
                    <div className="obStatsGrid">
                        <div className="obStatCard">
                            <div className="obStatIcon obStatIcon--blue"><User size={18} /></div>
                            <div>
                                <p className="obMuted">Total Offboardings</p>
                                <p className="obStatValue">{stats.total}</p>
                            </div>
                        </div>

                        <div className="obStatCard">
                            <div className="obStatIcon obStatIcon--coral"><Clock size={18} /></div>
                            <div>
                                <p className="obMuted">En cours</p>
                                <p className="obStatValue obStatValue--blue">{stats.enCours}</p>
                            </div>
                        </div>

                        <div className="obStatCard">
                            <p className="obMuted">Planifiés</p>
                            <p className="obStatValue">{stats.planifies}</p>
                        </div>

                        <div className="obStatCard">
                            <p className="obMuted">Terminés</p>
                            <p className="obStatValue obStatValue--green">{stats.termines}</p>
                        </div>
                    </div>

                    <div className="obCard obCard--nested">
                        <div className="obCard__head">
                            <h2 className="obCard__title">Départs à venir</h2>
                        </div>

                        <div className="obCard__body">
                            <div className="obUpcomingList">
                                {upcoming.length === 0 ? (
                                    <div className="obTinyMuted">Aucun départ à venir.</div>
                                ) : (
                                    upcoming.map((emp) => (
                                        <div key={emp.id} className="obUpcomingItem">
                                            <div className="obUpcomingLeft">
                                                <div className="obAvatarGrad">{avatarLetters(emp.employeeName)}</div>
                                                <div>
                                                    <p className="obStrong">{emp.employeeName}</p>
                                                    <p className="obSmallMuted">
                                                        {emp.jobLabel} • {emp.entityLabel}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="obUpcomingRight">
                                                <p className="obStrong">
                                                    {emp.departureDate
                                                        ? new Date(emp.departureDate).toLocaleDateString("fr-FR")
                                                        : "—"}
                                                </p>
                                                <p className="obTinyMuted">
                                                    {daysLeft(emp.departureDate) != null
                                                        ? `${daysLeft(emp.departureDate)} jours restants`
                                                        : ""}
                                                </p>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            <div style={{ height: 10 }} />
                            <div className="obTinyMuted">
                                Backend lists: users({(Array.isArray(users) ? users : []).length}) / jobs({jobs.length}) /
                                entites({entites.length}) — offboardings({offboardings.length})
                            </div>
                        </div>
                    </div>
            </div>
        );
    };

    /** ================= Employees List View ================= */
    const EmployeesListView = () => (
        <div style={{ padding: "0 14px" }}>
            <div className="obControls">
                <div className="obSearch">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Rechercher un collaborateur..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <button onClick={openModal} className="obBtn obBtn--primary">
                    <Plus size={18} />
                    <span>Nouvel Offboarding</span>
                </button>
            </div>

            <div className="obTableWrap" style={{ borderTop: "1px solid #e5e7eb" }}>
                    <table className="obTable">
                        <thead>
                        <tr>
                            <th>Collaborateur</th>
                            <th>Département</th>
                            <th>Date de départ</th>
                            <th>Statut</th>
                            <th>Progression</th>
                            <th className="is-right">Actions</th>
                        </tr>
                        </thead>

                        <tbody>
                        {filteredRows.map((emp) => (
                            <tr key={emp.id}>
                                <td>
                                    <div className="obEmpCell">
                                        <div className="obAvatarGrad obAvatarGrad--sm">
                                            {String(emp.employeeName || "")
                                                .split(" ")
                                                .filter(Boolean)
                                                .map((n) => n[0])
                                                .join("")
                                                .toUpperCase()
                                                .slice(0, 2)}
                                        </div>
                                        <div className="obEmpTxt">
                                            <div className="obStrong">{emp.employeeName}</div>
                                            <div className="obSmallMuted">{emp.jobLabel}</div>
                                        </div>
                                    </div>
                                </td>

                                <td>{emp.entityLabel}</td>
                                <td>
                                    {emp.departureDate ? new Date(emp.departureDate).toLocaleDateString("fr-FR") : "—"}
                                </td>

                                <td>
                                    <span className={STATUS_CLASS(emp.status)}>{STATUS_TEXT(emp.status)}</span>
                                </td>

                                <td>
                                    <div className="obProgressRow">
                                        <div className="obProgressBar">
                                            <div className="obProgressFill" style={{ width: `${emp.progression}%` }} />
                                        </div>
                                        <span className="obProgressPct">{emp.progression}%</span>
                                    </div>
                                </td>

                                <td className="is-right">
                                    <button onClick={() => openDetails(emp.raw)} className="obLink">
                                        Voir détails
                                    </button>
                                    <button
                                        onClick={() => removeOffboarding(emp.id)}
                                        className="obIconBtn"
                                        type="button"
                                        title="Supprimer"
                                        style={{ marginLeft: 8 }}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}

                        {filteredRows.length === 0 && (
                            <tr>
                                <td colSpan={6} className="obEmpty">
                                    Aucun résultat
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                {offLoading && (
                    <div className="obTinyMuted" style={{ padding: 14 }}>
                        Chargement...
                    </div>
                )}
            </div>
    );

    /** ================= Details View ================= */
    const EmployeeDetailsView = () => {
        if (!selectedOffboardingId) return <div style={{ padding: 14 }}>Aucun offboarding sélectionné</div>;

        const off = selectedOffboarding || offboardings.find((o) => o.id === selectedOffboardingId) || null;
        const employeeId = off?.employeeId ?? off?.employee_id ?? off?.employee ?? null;
        const employeeName = employeeId ? getUserLabel(employeeId) : "—";
        const u = employeeId ? userById.get(String(employeeId)) : null;
        const jobLabel = getJobLabel(u?.jobId || u?.job_id);
        const entityLabel = getEntLabel(u?.entityId || u?.entity_id);

        const depDate = off?.dateDepart || off?.date_depart || off?.departureDate;
        const raison = off?.raisonDepart || off?.raison_depart || off?.reason;
        const commentaire = off?.commentaire || off?.notes || "";

        const backendProg = off?.progression ?? off?.progress;
        const computedProg = computeProgressFromEtapes(selectedEtapes);
        const progression = typeof backendProg === "number" ? backendProg : computedProg;

        const getCategoryIcon = (category) => {
            const s = String(category || "").toLowerCase();
            if (s.includes("mater")) return <Package size={18} />;
            if (s.includes("ac")) return <Key size={18} />;
            if (s.includes("know")) return <FileText size={18} />;
            if (s.includes("admin")) return <User size={18} />;
            return <AlertCircle size={18} />;
        };

        const daysLeft = (() => {
            if (!depDate) return null;
            const d = new Date(depDate);
            if (Number.isNaN(d.getTime())) return null;
            return Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
        })();

        const doneCount = selectedEtapes.filter((i) => ETAPE_IS_DONE(i.statut)).length;

        return (
            <div style={{ padding: 0 }}>
                <div className="obCard__head">
                    <button
                        onClick={() => {
                            setSelectedOffboardingId(null);
                            setActiveTab("employees");
                        }}
                        className="obLink"
                        type="button"
                    >
                        ← Retour à la liste
                    </button>

                    <button onClick={refreshSelected} className="obLink" type="button" style={{ marginLeft: 10 }}>
                        Rafraîchir
                    </button>
                </div>

                <div className="obCard__body">
                    <div className="obEmpHead">
                        <div className="obEmpHeadLeft">
                            <div className="obAvatarGrad obAvatarGrad--lg">
                                {String(employeeName || "")
                                    .split(" ")
                                    .filter(Boolean)
                                    .map((n) => n[0])
                                    .join("")
                                    .toUpperCase()
                                    .slice(0, 2)}
                            </div>
                            <div>
                                <h2 className="obH2" style={{ margin: 0, color: "#2f4595", fontWeight: 700 }}>
                                    {employeeName}
                                </h2>
                                <p className="obMuted" style={{ marginTop: 6 }}>
                                    {jobLabel}
                                </p>
                                {/*<p className="obMuted" style={{ marginTop: 4 }}>*/}
                                {/*    {entityLabel} {employeeId ? `• ID: ${employeeId}` : ""}*/}
                                {/*</p>*/}
                            </div>
                        </div>

                        <span className={STATUS_CLASS(off?.statut || off?.status || (progression >= 100 ? "termine" : "en_cours"))}>
              {STATUS_TEXT(off?.statut || off?.status || (progression >= 100 ? "termine" : "en_cours"))}
            </span>
                    </div>

                    <div style={{ height: 14 }} />

                    <div className="obInfoGrid">
                        <div>
                            <p className="obMuted">Date de départ</p>
                            <p className="obStrong">{depDate ? new Date(depDate).toLocaleDateString("fr-FR") : "—"}</p>
                        </div>
                        <div>
                            <p className="obMuted">Raison</p>
                            <p className="obStrong">{RAISON_FROM_BACK(raison)}</p>
                        </div>
                        <div>
                            <p className="obMuted">Jours restants</p>
                            <p className="obStrong">{daysLeft != null ? `${daysLeft} jours` : "—"}</p>
                        </div>
                    </div>

                    {commentaire ? (
                        <>
                            <div style={{ height: 10 }} />
                            <div className="obCard" style={{ margin: 0 }}>
                                <div className="obCard__head">
                                    <h3 className="obCard__title">Commentaire</h3>
                                </div>
                                <div className="obCard__body">
                                    <p className="obMuted" style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                                        {commentaire}
                                    </p>
                                </div>
                            </div>
                        </>
                    ) : null}

                    <div style={{ height: 14 }} />

                    <h3 className="obCard__title" style={{ margin: "0 0 10px 0" }}>
                        Progression globale
                    </h3>

                    <div className="obProgressRow">
                        <div className="obProgressBar" style={{ height: 10 }}>
                            <div className="obProgressFill" style={{ width: `${progression}%` }} />
                        </div>
                        <span className="obProgressPct">{progression}%</span>
                    </div>

                    <p className="obMuted" style={{ marginTop: 10 }}>
                        {doneCount} sur {selectedEtapes.length} étapes complétées
                    </p>

                    <div style={{ height: 14 }} />

                    <div className="obCard" style={{ margin: 0 }}>
                        <div className="obCard__head" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <h3 className="obCard__title">Checklist d'offboarding</h3>

                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <input
                                    className="obSearchInput"
                                    style={{ width: 240 }}
                                    placeholder="Nom de l'étape..."
                                    value={etapeDraft.nom}
                                    onChange={(e) => setEtapeDraft((p) => ({ ...p, nom: e.target.value }))}
                                />
                                <select
                                    className="obSearchInput ordreSelect"
                                    style={{ width: 120 }}
                                    value={etapeDraft.ordreEtape}
                                    onChange={(e) =>
                                        setEtapeDraft((p) => ({ ...p, ordreEtape: e.target.value }))
                                    }
                                >
                                    <option value="">Ordre</option>

                                    {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => {
                                        const isUsed = selectedEtapes.some(
                                            (etape) => etape.ordreEtape === num
                                        );

                                        return (
                                            <option
                                                key={num}
                                                value={num}
                                                disabled={isUsed}
                                                className={isUsed ? "option-disabled" : ""}
                                            >
                                                {num}
                                            </option>
                                        );
                                    })}
                                </select>

                                <select
                                    className="obSearchInput"
                                    style={{ width: 160 }}
                                    value={etapeDraft.statut}
                                    onChange={(e) => setEtapeDraft((p) => ({ ...p, statut: e.target.value }))}
                                >
                                    {ETAPE_STATUS_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>

                                <button className="obBtn obBtn--primary" type="button" onClick={addEtape} disabled={!etapeDraft.nom.trim()}>
                                    <Plus size={18} />
                                    <span>Ajouter</span>
                                </button>
                            </div>
                        </div>

                        <div className="obCard__body">
                            <div className="obChecklist">
                                {selectedEtapes.length === 0 ? (
                                    <div className="obTinyMuted">Aucune étape.</div>
                                ) : (
                                    selectedEtapes.map((item) => {
                                        const isDone = ETAPE_IS_DONE(item.statut);
                                        return (
                                            <div key={item.id} className={`obCheckItem ${isDone ? "is-done" : ""}`}>
                                                <div className="obCheckLeft">
                                                    <button
                                                        type="button"
                                                        onClick={() => completeEtapeOnClick(item)}
                                                        className="obIconBtn"
                                                        title="Compléter"
                                                    >
                                                        {isDone ? <CheckCircle size={18} /> : <Clock size={18} />}
                                                    </button>

                                                    <span className="obMuted" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                            {getCategoryIcon(item.category)} {item.nom || item.task || "—"}
                          </span>

                                                    <span className="obTinyMuted" style={{ marginLeft: 10 }}>
                            (ordre: {item.ordreEtape ?? "—"})
                          </span>
                                                </div>

                                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    {isDone ? (
                                                        <span className="obBadge obBadge--green">✓ Complété</span>
                                                    ) : (
                                                        <span className="obBadge obBadge--gray">En attente</span>
                                                    )}

                                                    <button onClick={() => removeEtape(item)} className="obIconBtn" type="button" title="Supprimer">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {offLoading && (
                                <div className="obTinyMuted" style={{ marginTop: 10 }}>
                                    Chargement...
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    /** ================= Modal (nouveau design attestation) ================= */
    const NewOffboardingModal = () => (
        <Modal
            open={showModal}
            title="Préparer un offboarding"
            onClose={closeModal}
            footer={
                <>
                    <button className="obBtn obBtn--ghost" type="button" onClick={closeModal}>
                        Annuler
                    </button>

                    <button
                        className="obBtn obBtn--primary"
                        type="button"
                        onClick={createOffboarding}
                        disabled={!canCreate}
                    >
                        Créer l'offboarding
                    </button>
                </>
            }
        >
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {/* Filtres à côté du collaborateur */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px" }}>
                    <SearchableSelect
                        label="Collaborateur *"
                        placeholder={loadingLists ? "Chargement..." : "Tape pour filtrer (ex: S)"}
                        options={employeeOptionsFiltered}
                        value={form.employeeId}
                        onChange={(val, opt) => {
                            handleForm("employeeId", val);
                            onPickEmployee(val, opt);
                        }}
                        disabled={loadingLists}
                    />

                    <SearchableSelect
                        label="Poste"
                        placeholder="Filtrer par poste"
                        options={positionOptionsFiltered}
                        value={filters.jobId}
                        onChange={(val) => onPickJob(val)}
                        disabled={loadingLists}
                    />

                    <SearchableSelect
                        label="Département"
                        placeholder="Filtrer par département"
                        options={departmentOptionsFiltered}
                        value={filters.entityId}
                        onChange={(val) => onPickEntity(val)}
                        disabled={loadingLists}
                    />
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
                    <button
                        className="obBtn obBtn--ghost"
                        type="button"
                        onClick={() => {
                            clearFilters();
                            handleForm("employeeId", "");
                        }}
                        style={{ fontSize: "13px", padding: "4px 8px" }}
                    >
                        Réinitialiser filtres
                    </button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginTop: 8 }}>
                    <div className="obField">
                        <label>Date de départ prévue *</label>
                        <input
                            type="date"
                            value={form.departureDate}
                            onChange={(e) => handleForm("departureDate", e.target.value)}
                        />
                    </div>

                    <div className="obField">
                        <label>Raison du départ</label>
                        <select value={form.reason} onChange={(e) => handleForm("reason", e.target.value)}>
                            {RAISON_UI.map((r) => (
                                <option key={r}>{r}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="obField">
                    <label>Notes additionnelles</label>
                    <textarea
                        rows={4}
                        placeholder="Informations importantes à noter..."
                        value={form.notes}
                        onChange={(e) => handleForm("notes", e.target.value)}
                    />
                </div>

                {!canCreate && (
                    <div className="obHint" style={{ marginTop: 8 }}>
                        * Collaborateur + date de départ sont obligatoires.
                    </div>
                )}
            </div>
        </Modal>
    );

    /** ================= Render ================= */
    return (
        <div className="obPage">
            <div className="obWrap">
                <div className="obHeader">
                    <div>
                        <h1>Gestion des Offboardings</h1>
                        <button onClick={openModal} className="obBtn obBtn--primary obHeaderActionMobile" type="button">
                            <Plus size={18} />
                            <span>Nouvel Offboarding</span>
                        </button>
                        <p>Suivi des départs et checklist de clôture (accès, matériel, administratif…)</p>
                    </div>
                </div>

                <div className="obCard obCard--noPad">
                    <div className="obTabs">
                        <button
                            onClick={() => {
                                setActiveTab("dashboard");
                                setSelectedOffboardingId(null);
                            }}
                            className={`obTab ${activeTab === "dashboard" ? "is-active" : ""}`}
                            type="button"
                        >
                            Tableau de bord
                        </button>

                        <button
                            onClick={() => {
                                setActiveTab("employees");
                                setSelectedOffboardingId(null);
                            }}
                            className={`obTab ${activeTab === "employees" || activeTab === "details" ? "is-active" : ""}`}
                            type="button"
                        >
                            Liste des départs
                        </button>
                    </div>

                    <div style={{ paddingBottom: "14px" }}>
                        {activeTab === "dashboard" && DashboardView()}
                        {activeTab === "employees" && EmployeesListView()}
                        {activeTab === "details" && EmployeeDetailsView()}
                    </div>
                </div>
            </div>

            {showModal && NewOffboardingModal()}
        </div>
    );
}
